<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import EmployeePickerModal from '../components/EmployeePickerModal.vue'
import ReasonPromptModal from '../components/ReasonPromptModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney } from '../utils/format'
import type {
  CurrencyOption,
  EmployeeOption,
  PayrollAdjustment,
  PayrollAdjustmentType,
  PayrollPeriod,
} from '../types/finance'

/**
 * `/payroll/adjustments` — manual payroll items.
 * `bonus`/`correction` become run earning lines; `deduction`/`advance`/`penalty`
 * become deduction lines.
 */

const TYPES: PayrollAdjustmentType[] = ['bonus', 'deduction', 'advance', 'penalty', 'correction']

const { t } = useI18n()
const auth = useAuthStore()
const {
  listPayrollAdjustments,
  listPayrollPeriods,
  listCurrencies,
  createPayrollAdjustment,
  approvePayrollAdjustment,
  rejectPayrollAdjustment,
  reversePayrollAdjustment,
} = useFinanceApi()

const PER_PAGE = 25
const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('payroll_adjustments.manage'))
const canApprove = computed(() => permissions.value.includes('payroll_adjustments.approve'))

const rows = ref<PayrollAdjustment[]>([])
const periods = ref<PayrollPeriod[]>([])
const currencies = ref<CurrencyOption[]>([])
const total = ref(0)
const totalPages = ref(1)
const page = ref(1)
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<string | number | null>(null)

const filters = ref({
  payroll_period_id: '' as string | number,
  status: '',
  type: '',
  currency_code: '',
})

const pickerOpen = ref(false)
const selectedEmployee = ref<EmployeeOption | null>(null)
const saving = ref(false)
const pending = ref<{ action: 'reject' | 'reverse'; adjustment: PayrollAdjustment } | null>(null)

const formOpen = ref(false)
const form = reactive({
  payroll_period_id: '' as string | number,
  type: 'bonus' as PayrollAdjustmentType,
  amount: '' as string | number,
  currency_code: '',
  reason: '',
})

const formError = computed(() => {
  if (!selectedEmployee.value) return t('finance.payrollFlow.adjustments.selectEmployee')
  if (!form.payroll_period_id) return t('finance.payrollFlow.adjustments.periodRequired')
  if (!Number(form.amount) || Number(form.amount) <= 0) return t('finance.payrollFlow.adjustments.amountRequired')
  if (!form.currency_code) return t('finance.payrollFlow.adjustments.currencyRequired')
  if (!form.reason.trim()) return t('finance.common.requiredField', { field: t('finance.common.reason') })
  return ''
})

const rangeLabel = computed(() =>
  total.value ? t('finance.common.rows', { count: total.value }) : '',
)

function typeLabel(type: string): string {
  const key = `finance.payrollFlow.adjustmentType${type.charAt(0).toUpperCase()}${type.slice(1)}`
  const label = t(key)
  return label.startsWith('finance.') ? type : label
}

function statusLabel(status: string): string {
  const key = `finance.payrollFlow.adjustmentStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`
  const label = t(key)
  return label.startsWith('finance.') ? status : label
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listPayrollAdjustments({
      payroll_period_id: filters.value.payroll_period_id,
      status: filters.value.status || undefined,
      type: filters.value.type || undefined,
      currency_code: filters.value.currency_code.trim() || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    rows.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
    rows.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

async function loadOptions() {
  try {
    const [periodResult, currencyList] = await Promise.all([
      listPayrollPeriods({ page: 1, per_page: 50 }),
      listCurrencies(),
    ])
    periods.value = periodResult.items
    currencies.value = currencyList
    if (!form.currency_code && currencyList.length) {
      form.currency_code = currencyList.find((entry) => entry.is_base)?.code ?? currencyList[0].code
    }
  } catch {
    periods.value = []
    currencies.value = []
  }
}

function openForm() {
  formOpen.value = true
  actionError.value = ''
  actionMessage.value = ''
  selectedEmployee.value = null
  form.reason = ''
  form.amount = ''
  form.type = 'bonus'
  if (!form.payroll_period_id && periods.value.length) {
    form.payroll_period_id = periods.value.find((period) => period.status === 'open')?.id ?? periods.value[0].id
  }
}

async function submit() {
  actionError.value = ''
  actionMessage.value = ''
  if (formError.value) {
    actionError.value = formError.value
    return
  }
  saving.value = true
  try {
    const employee = selectedEmployee.value
    if (!employee) return
    await createPayrollAdjustment({
      employee_id: employee.id,
      payroll_period_id: form.payroll_period_id,
      type: form.type,
      amount: Number(form.amount),
      currency_code: form.currency_code,
      reason: form.reason.trim(),
    })
    actionMessage.value = t('finance.payrollFlow.adjustments.created')
    formOpen.value = false
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

async function approve(adjustment: PayrollAdjustment) {
  busyId.value = adjustment.id
  actionError.value = ''
  actionMessage.value = ''
  try {
    await approvePayrollAdjustment(adjustment.id)
    actionMessage.value = t('finance.payrollFlow.adjustments.doneApproved')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

async function confirmReason(reason: string) {
  const pendingAction = pending.value
  if (!pendingAction) return
  busyId.value = pendingAction.adjustment.id
  actionError.value = ''
  actionMessage.value = ''
  try {
    if (pendingAction.action === 'reject') {
      await rejectPayrollAdjustment(pendingAction.adjustment.id, reason)
      actionMessage.value = t('finance.payrollFlow.adjustments.doneRejected')
    } else {
      await reversePayrollAdjustment(pendingAction.adjustment.id, reason)
      actionMessage.value = t('finance.payrollFlow.adjustments.doneReversed')
    }
    pending.value = null
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

watch(
  () => [
    filters.value.payroll_period_id,
    filters.value.status,
    filters.value.type,
    filters.value.currency_code,
  ],
  () => {
    page.value = 1
    void load()
  },
)

onMounted(() => {
  void loadOptions()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.adjustments.eyebrow') }}</p>
      <h2>{{ t('finance.payrollFlow.adjustments.title') }}</h2>
      <p>{{ t('finance.payrollFlow.adjustments.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="filters.payroll_period_id" class="filter-select">
      <option value="">{{ t('finance.payrollFlow.allPeriods') }}</option>
      <option v-for="period in periods" :key="period.id" :value="period.id">
        {{ period.code }} · {{ period.name }}
      </option>
    </select>
    <select v-model="filters.type" class="filter-select">
      <option value="">{{ t('finance.common.allTypes') }}</option>
      <option v-for="type in TYPES" :key="type" :value="type">{{ typeLabel(type) }}</option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option value="draft">{{ statusLabel('draft') }}</option>
      <option value="approved">{{ statusLabel('approved') }}</option>
      <option value="rejected">{{ statusLabel('rejected') }}</option>
      <option value="used">{{ statusLabel('used') }}</option>
      <option value="reversed">{{ statusLabel('reversed') }}</option>
    </select>
    <input v-model="filters.currency_code" class="filter-select" type="text" placeholder="UZS">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.refresh') }}
    </button>
    <span class="spacer" />
    <button v-if="canManage" class="btn-primary" type="button" @click="openForm">
      {{ t('finance.payrollFlow.adjustments.create') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <section v-if="formOpen" class="form-card">
    <h3>
      {{ t('finance.payrollFlow.adjustments.create') }}
      <small>{{ t('finance.payrollFlow.adjustments.typeHint') }}</small>
    </h3>
    <div class="form-grid tight">
      <div class="field">
        <label>{{ t('finance.payrollFlow.adjustments.employee') }}</label>
        <button class="field-button" type="button" @click="pickerOpen = true">
          <span :class="{ placeholder: !selectedEmployee }">
            {{ selectedEmployee ? selectedEmployee.name : t('finance.payrollFlow.adjustments.selectEmployee') }}
          </span>
        </button>
      </div>
      <div class="field">
        <label for="adjustment-period">{{ t('finance.payrollFlow.adjustments.period') }}</label>
        <select id="adjustment-period" v-model="form.payroll_period_id">
          <option value="">{{ t('finance.payrollFlow.selectPeriod') }}</option>
          <option v-for="period in periods" :key="period.id" :value="period.id">
            {{ period.code }} · {{ period.status }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="adjustment-type">{{ t('finance.common.type') }}</label>
        <select id="adjustment-type" v-model="form.type">
          <option v-for="type in TYPES" :key="type" :value="type">{{ typeLabel(type) }}</option>
        </select>
      </div>
      <div class="field">
        <label for="adjustment-amount">{{ t('finance.common.amount') }}</label>
        <input id="adjustment-amount" v-model="form.amount" type="number" min="0" step="0.01">
      </div>
      <div class="field">
        <label for="adjustment-currency">{{ t('finance.common.currency') }}</label>
        <select id="adjustment-currency" v-model="form.currency_code">
          <option value=""></option>
          <option v-for="currency in currencies" :key="currency.code" :value="currency.code">
            {{ currency.code }}
          </option>
        </select>
      </div>
      <div class="field field-wide">
        <label for="adjustment-reason">{{ t('finance.common.reason') }}</label>
        <input id="adjustment-reason" v-model="form.reason" type="text">
      </div>
    </div>
    <p v-if="formError" class="error-inline">{{ formError }}</p>
    <div class="modal-actions">
      <button class="btn-secondary" type="button" @click="formOpen = false">
        {{ t('finance.common.cancel') }}
      </button>
      <button class="btn-primary" type="button" :disabled="saving || Boolean(formError)" @click="submit">
        {{ saving ? t('finance.common.saving') : t('finance.common.create') }}
      </button>
    </div>
  </section>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <p v-else-if="!rows.length" class="state-message">{{ t('finance.payrollFlow.adjustments.empty') }}</p>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payrollFlow.adjustments.employee') }}</th>
            <th>{{ t('finance.payrollFlow.adjustments.period') }}</th>
            <th>{{ t('finance.common.type') }}</th>
            <th class="cell-num">{{ t('finance.common.amount') }}</th>
            <th>{{ t('finance.common.reason') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="adjustment in rows" :key="adjustment.id">
            <td class="cell-name">
              {{ adjustment.employee_name }}
              <span class="cell-sub">{{ adjustment.employee_code || '—' }}</span>
            </td>
            <td class="cell-code">{{ adjustment.payroll_period_code || '—' }}</td>
            <td>{{ typeLabel(adjustment.type) }}</td>
            <td class="cell-num">{{ formatMoney(adjustment.amount, adjustment.currency_code) }}</td>
            <td>{{ adjustment.reason }}</td>
            <td>
              <span class="status-badge" :class="`status-${adjustment.status}`">
                {{ statusLabel(adjustment.status) }}
              </span>
            </td>
            <td class="cell-actions">
              <button
                v-if="canApprove && adjustment.status === 'draft'"
                class="link-action"
                type="button"
                :disabled="busyId === adjustment.id"
                @click="approve(adjustment)"
              >
                {{ t('finance.payrollFlow.approve') }}
              </button>
              <button
                v-if="canApprove && adjustment.status === 'draft'"
                class="link-action danger"
                type="button"
                :disabled="busyId === adjustment.id"
                @click="pending = { action: 'reject', adjustment }"
              >
                {{ t('finance.payrollFlow.reject') }}
              </button>
              <button
                v-if="canManage && (adjustment.status === 'approved' || adjustment.status === 'used')"
                class="link-action danger"
                type="button"
                :disabled="busyId === adjustment.id"
                @click="pending = { action: 'reverse', adjustment }"
              >
                {{ t('finance.payrollFlow.reverse') }}
              </button>
              <span
                v-if="
                  !(canApprove && adjustment.status === 'draft') &&
                  !(canManage && (adjustment.status === 'approved' || adjustment.status === 'used'))
                "
                class="empty-inline"
              >
                —
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button class="btn-secondary" type="button" :disabled="page <= 1" @click="page = page - 1; load()">
        {{ t('finance.common.previous') }}
      </button>
      <button
        class="btn-secondary"
        type="button"
        :disabled="page >= totalPages"
        @click="page = page + 1; load()"
      >
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>

  <EmployeePickerModal
    :open="pickerOpen"
    :selected-id="selectedEmployee?.id ?? null"
    @close="pickerOpen = false"
    @select="(employee) => { selectedEmployee = employee; pickerOpen = false }"
  />

  <ReasonPromptModal
    :open="Boolean(pending)"
    :title="pending?.action === 'reject' ? t('finance.payrollFlow.rejectTitle') : t('finance.payrollFlow.reverseTitle')"
    :message="pending?.action === 'reject' ? t('finance.payrollFlow.rejectMessage') : t('finance.payrollFlow.reverseMessage')"
    :busy="Boolean(busyId)"
    @close="pending = null"
    @confirm="confirmReason"
  />
</template>

<style scoped>
.status-draft {
  border-color: #cfd8d1;
  color: #5f6b62;
  background: #f6f8f6;
}

.status-approved {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-used {
  border-color: #7fa6d9;
  color: #274b7a;
  background: #f0f5fb;
}

.status-rejected,
.status-reversed {
  border-color: #e0b0a6;
  color: #a13c2c;
  background: #fdf3f1;
}
</style>
